/**
 * 915-Low CE Breakout — SENSEX paper/forward-test. On the day's first low, takes
 * ATM/ITM1/ITM2 CE strikes that held their 09:15 candle low and, while SPOT is still
 * holding its 09:15 low (the gate), buys a breakout above the morning low candle:
 * entry = lowCandle.high, SL = lowCandle.low - 10, target = entry + 100 premium pts.
 * SENSEX only, paper. Observe ~10 sessions for a verdict.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "/api/zerodha";
const fmtTime = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-";

function exportCSV(trades) {
  if (!trades.length) return;
  const h = "Session,Strike,Money,Symbol,Qty,LowCandle,LowCandleLow,Entry,SL,Target,SpotHeld,Exit,PnLpts,PnL,Result,ExitReason,EntryTime,ExitTime\n";
  const rows = trades.map(t => `${t.session},${t.strike},${t.money},${t.symbol},${t.qty},${t.low_candle_time},${t.low_candle_low},${t.entry},${t.sl},${t.target},${t.spot_held_at_entry},${t.exit_price ?? ""},${t.pnl_pts ?? ""},${t.pnl || 0},${t.result || "OPEN"},${t.exit_reason || ""},${t.entry_time},${t.exit_time || ""}`).join("\n");
  const b = new Blob([h + rows], { type: "text/csv" }); const a = document.createElement("a");
  a.href = URL.createObjectURL(b); a.download = "breakout915-trades.csv"; a.click();
}

export default function Breakout915() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const poll = async () => { try { const r = await axios.get(`${API}/breakout915/status`, { timeout: 10000 }); if (r.data.success) setStatus(r.data); } catch {} };
  useEffect(() => { poll(); pollRef.current = setInterval(poll, 10000); return () => clearInterval(pollRef.current); }, []);
  const start = async () => { setLoading(true); try { await axios.post(`${API}/breakout915/start`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const stop = async () => { setLoading(true); try { await axios.post(`${API}/breakout915/stop`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const clear = async () => { if (!window.confirm("Clear closed 915-breakout trades?")) return; try { await axios.post(`${API}/breakout915/clear`); } catch {} setTimeout(poll, 500); };

  const trades = status?.trades || [];
  const open = trades.filter(t => t.status === "OPEN");
  const closed = trades.filter(t => t.status === "CLOSED");
  const sess = status?.session || {};

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>915-Low CE Breakout <span style={{ fontSize: 12, color: "#a78bfa" }}>(SENSEX ATM+2 ITM, paper)</span></h2>
          <p className="dashboard-subtitle">On the day's first low, takes CE strikes that held their 09:15 candle low and — only while SPOT holds its 09:15 low — buys the breakout above the morning low candle. SL = low−10, target = +100 premium pts. Observe ~10 sessions.</p>
        </div>
        <div className="dashboard-actions">
          <button className={`btn ${status?.active ? "btn-danger" : "btn-primary"}`} onClick={status?.active ? stop : start} disabled={loading}>
            {status?.active ? "Stop Scanner" : "Start Scanner"}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: status.active ? "var(--tint-pos)" : "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text)", flexWrap: "wrap", alignItems: "center" }}>
          <span>{status.active ? <span style={{ color: "#34d399" }}>SCANNING</span> : "STOPPED"}</span>
          <span>Checks: {status.scanCount}</span>
          {status.lastScanTime && <span>Last: {new Date(status.lastScanTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}</span>}
          <span>Market: {status.marketOpen ? <span style={{ color: "#34d399" }}>OPEN</span> : <span style={{ color: "#f87171" }}>CLOSED</span>}</span>
          <span style={{ color: "var(--edge)" }}>|</span>
          <span>Sessions: <strong>{status.sessionsObserved}/10</strong></span>
          <span>Target hit: <strong>{status.targetsHit}</strong>/{status.closed}</span>
          <span>WR: <strong>{status.winRate}%</strong></span>
          <span>Booked: <strong className={status.totalPnlPts >= 0 ? "positive" : "negative"}>{status.totalPnlPts > 0 ? "+" : ""}{status.totalPnlPts} pts</strong> (<span className={status.totalPnl >= 0 ? "positive" : "negative"}>₹{status.totalPnl}</span>)</span>
        </div>
      )}

      {sess.date && (
        <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--grey-text2)", borderBottom: "1px solid var(--surface2)" }}>
          Today {sess.date}: ATM <strong>{sess.atm}</strong> · exp {sess.expiry} · spot 09:15 low <strong>{sess.spot_915_low}</strong> · gate {sess.spot_broke
            ? <span style={{ color: "#f87171" }}>CLOSED (spot broke its 09:15 low)</span>
            : <span style={{ color: "#34d399" }}>OPEN (spot holding)</span>}
        </div>
      )}

      {open.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13, color: "#f59e0b" }}>Open Positions ({open.length})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Money</th><th>Symbol</th><th>Strike</th><th>Qty</th><th>Low Candle</th><th>Entry</th><th>SL</th><th>Target</th><th>Spot Held</th><th>Entry Time</th></tr></thead>
              <tbody>
                {open.map(t => (
                  <tr key={t.pos_key} className="trade-open-row">
                    <td><span className="signal-badge badge-long">{t.money}</span></td>
                    <td style={{ fontSize: 9, color: "var(--grey-text2)" }}>{t.symbol}</td>
                    <td>{t.strike}</td><td>{t.qty}</td>
                    <td style={{ fontSize: 10 }}>{t.low_candle_time} ({t.low_candle_low})</td>
                    <td><strong>{t.entry}</strong></td><td className="sl">{t.sl}</td><td style={{ color: "#34d399" }}>{t.target}</td>
                    <td>{t.spot_held_at_entry ? "✓" : "✗"}</td><td style={{ fontSize: 10 }}>{fmtTime(t.entry_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ padding: "8px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 13, color: "var(--grey-text)" }}>Closed Trades ({closed.length})</h4>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-export" onClick={() => exportCSV(trades)}>Export CSV</button>
            {closed.length > 0 && <button className="btn btn-sm btn-danger" onClick={clear}>Clear</button>}
          </div>
        </div>
        {closed.length > 0 && (
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Session</th><th>Money</th><th>Strike</th><th>Entry</th><th>SL</th><th>Target</th><th>Exit</th><th>P&L pts</th><th>Result</th><th>Exit Reason</th><th>Exit Time</th></tr></thead>
              <tbody>
                {closed.map(t => (
                  <tr key={t.pos_key} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                    <td style={{ fontSize: 10 }}>{t.session}</td>
                    <td><strong style={{ color: "#34d399" }}>{t.money}</strong></td>
                    <td>{t.strike}</td><td>{t.entry}</td><td className="sl">{t.sl}</td><td style={{ color: "#34d399" }}>{t.target}</td>
                    <td>{t.exit_price}</td>
                    <td className={t.pnl_pts >= 0 ? "positive" : "negative"}>{t.pnl_pts > 0 ? "+" : ""}{t.pnl_pts}</td>
                    <td><span style={{ color: t.result === "WIN" ? "#34d399" : "#f87171", fontWeight: 700 }}>{t.result || "?"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.exit_reason}</td><td style={{ fontSize: 10 }}>{fmtTime(t.exit_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!status?.active && closed.length === 0 && open.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Click <strong>Start Scanner</strong> to forward-test the 915-Low CE Breakout on SENSEX.</p>
          <p style={{ fontSize: 12 }}>Scans SENSEX 5-min during market hours. Only arms when spot holds its 09:15 low; enters held ATM/ITM CE on a breakout above the morning low candle. Let it run ~10 sessions.</p>
        </div>
      )}
    </div>
  );
}
